// Automation & Integration Category
import { ShowcaseCategory } from "../showcase-types.ts";
import { ICONS } from "../showcase-icons.ts";

export const automationIntegrationCategory: ShowcaseCategory = {
  id: "automation-integration",
  title: {
    en: "Automation & Integration",
    lv: "Automatizācija un Integrācija",
    it: "Automazione e Integrazione",
  },
  subtitle: {
    en: "Seamless connections between systems and automated workflows",
    lv:
      "Nevainojamas savienojumi starp sistēmām un automatizētas darba plūsmas",
    it: "Connessioni perfette tra sistemi e flussi di lavoro automatizzati",
  },
  iconGradient: "from-blue-500 to-cyan-600",
  iconSvgPath: ICONS.lightning,
  projects: [
    {
      id: "b2c-ecommerce",
      title: {
        en: "Complete eCommerce Ecosystem Integration",
        lv: "Pilnīga e-komercijas Ekosistēmas Integrācija",
        it: "Integrazione Completa dell'Ecosistema eCommerce",
      },
      industry: {
        en: "Cosmetics Brand",
        lv: "Kosmētikas Zīmols",
        it: "Marchio Cosmetico",
      },
      description: {
        en:
          "We architected and implemented a comprehensive integration connecting Shopify stores, Salesforce CRM, custom PIM, BMD ERP, and Sendcloud shipping. This unified backbone automates order fulfillment, inventory sync, customer data management, and returns processing—enabling the company to scale B2C operations without operational friction.",
        lv:
          "Mēs izstrādājām un ieviesām visaptverošu integrāciju, kas savieno Shopify veikalus, Salesforce CRM, pielāgotu PIM, BMD ERP un Sendcloud piegādi. Šī vienotā mugurkauls automatizē pasūtījumu izpildi, krājumu sinhronizāciju, klientu datu pārvaldību un atgriešanas apstrādi—ļaujot uzņēmumam paplašināt B2C darbības bez darbības berzes.",
        it:
          "Abbiamo progettato e implementato un'integrazione completa che collega negozi Shopify, Salesforce CRM, PIM personalizzato, BMD ERP e spedizione Sendcloud. Questa spina dorsale unificata automatizza l'evasione degli ordini, la sincronizzazione dell'inventario, la gestione dei dati dei clienti e l'elaborazione dei resi, consentendo all'azienda di scalare le operazioni B2C senza attriti operativi.",
      },
      keyOutcomes: [
        {
          text: {
            en: "End-to-end order automation from checkout to fulfillment",
            lv: "Pilnīga pasūtījumu automatizācija no apmaksas līdz izpildei",
            it: "Automazione completa degli ordini dal checkout all'evasione",
          },
        },
        {
          text: {
            en: "Real-time inventory synchronization across all channels",
            lv: "Reāllaika krājumu sinhronizācija visos kanālos",
            it:
              "Sincronizzazione dell'inventario in tempo reale su tutti i canali",
          },
        },
        {
          text: {
            en: "Automated returns and shipping management",
            lv: "Automatizēta atgriešanas un piegādes pārvaldība",
            it: "Gestione automatizzata dei resi e delle spedizioni",
          },
        },
      ],
      techBadges: [
        { name: "Node.js", colorClass: "bg-blue-100 text-blue-700" },
        { name: "Shopify", colorClass: "bg-green-100 text-green-700" },
        { name: "Salesforce", colorClass: "bg-indigo-100 text-indigo-700" },
        { name: "MongoDB", colorClass: "bg-purple-100 text-purple-700" },
        { name: "BMD ERP", colorClass: "bg-orange-100 text-orange-700" },
        { name: "Sendcloud", colorClass: "bg-cyan-100 text-cyan-700" },
      ],
      metric: {
        value: "100%",
        label: { en: "Automated", lv: "Automatizēts", it: "Automatizzato" },
        sublabel: {
          en: "order processing",
          lv: "pasūtījumu apstrāde",
          it: "elaborazione ordini",
        },
        colorClass: "text-blue-600",
        bgGradient: "from-blue-50 to-cyan-50",
      },
      iconGradient: "from-blue-500 to-cyan-600",
      iconSvgPath: ICONS.shoppingCart,
    },
    {
      id: "b2b-manufacturing",
      title: {
        en: "B2B Manufacturing Sales Pipeline",
        lv: "B2B Ražošanas Pārdošanas Konveijers",
        it: "Pipeline di Vendita Manifatturiero B2B",
      },
      industry: {
        en: "Industrial Manufacturing",
        lv: "Rūpnieciskā Ražošana",
        it: "Produzione Industriale",
      },
      description: {
        en:
          "We built a custom integration between Salesforce CRM and Epicor ERP, creating a seamless flow from opportunity to fulfillment. Sales teams work in Salesforce while manufacturing operations run in Epicor—with our middleware ensuring data consistency, automated quote generation, and real-time visibility across both systems.",
        lv:
          "Mēs izveidojām pielāgotu integrāciju starp Salesforce CRM un Epicor ERP, radot nevainojamu plūsmu no iespējas līdz izpildei. Pārdošanas komandas strādā Salesforce, kamēr ražošanas darbības notiek Epicor—ar mūsu starpslāni, kas nodrošina datu konsekvenci, automatizētu piedāvājumu ģenerēšanu un reāllaika redzamību abās sistēmās.",
        it:
          "Abbiamo costruito un'integrazione personalizzata tra Salesforce CRM ed Epicor ERP, creando un flusso senza soluzione di continuità dall'opportunità all'evasione. I team di vendita lavorano in Salesforce mentre le operazioni di produzione vengono eseguite in Epicor, con il nostro middleware che garantisce coerenza dei dati, generazione automatica di preventivi e visibilità in tempo reale su entrambi i sistemi.",
      },
      keyOutcomes: [
        {
          text: {
            en: "Unified sales and manufacturing data flow",
            lv: "Vienota pārdošanas un ražošanas datu plūsma",
            it: "Flusso di dati unificato di vendite e produzione",
          },
        },
        {
          text: {
            en: "Automated quote-to-order conversion",
            lv: "Automatizēta piedāvājuma pārvēršana pasūtījumā",
            it: "Conversione automatizzata da preventivo a ordine",
          },
        },
        {
          text: {
            en: "Real-time inventory and production visibility for sales",
            lv: "Reāllaika krājumu un ražošanas redzamība pārdošanai",
            it:
              "Visibilità in tempo reale dell'inventario e della produzione per le vendite",
          },
        },
      ],
      techBadges: [
        { name: "Node.js", colorClass: "bg-blue-100 text-blue-700" },
        { name: "Salesforce", colorClass: "bg-indigo-100 text-indigo-700" },
        { name: "Epicor ERP", colorClass: "bg-orange-100 text-orange-700" },
        { name: "REST APIs", colorClass: "bg-green-100 text-green-700" },
        { name: "PostgreSQL", colorClass: "bg-purple-100 text-purple-700" },
      ],
      metric: {
        value: "75%",
        label: { en: "Faster", lv: "Ātrāk", it: "Più Veloce" },
        sublabel: {
          en: "quote generation",
          lv: "piedāvājumu ģenerēšana",
          it: "generazione preventivi",
        },
        colorClass: "text-purple-600",
        bgGradient: "from-purple-50 to-pink-50",
      },
      iconGradient: "from-purple-500 to-pink-600",
      iconSvgPath: ICONS.building,
    },
    {
      id: "tpm-crm-integration",
      title: {
        en: "TPM / CRM Integration",
        lv: "TPM / CRM Integrācija",
        it: "Integrazione TPM / CRM",
      },
      industry: {
        en: "Consumer Goods Company",
        lv: "Patēriņa Preču Uzņēmums",
        it: "Azienda di Beni di Consumo",
      },
      description: {
        en:
          "We delivered a connecting integration between Trade Promotion Management (TPM) system and Salesforce CRM. A third-party agency handled the actual build (after we provided technical guidance and governance on the integration, and directly owned the CRM configuration and customization). The business needed centralized, reliable synchronization—without disrupting either team's hands. The project established an official, supervised data bridge between the two systems, ensuring data consistency and enabling seamless promotion management.",
        lv:
          "Mēs piegādājām savienojuma integrāciju starp Tirdzniecības Veicināšanas Pārvaldības (TPM) sistēmu un Salesforce CRM. Trešās puses aģentūra veica faktisko būvniecību (pēc tam, kad mēs sniedzām tehnisko vadību un pārvaldību par integrāciju, un tieši piederēja CRM konfigurācija un pielāgošana). Biznesam bija nepieciešama centralizēta, uzticama sinhronizācija—netraucējot nevienas komandas rokām. Projekts izveidoja oficiālu, uzraudzītu datu tiltu starp abām sistēmām, nodrošinot datu konsekvenci un ļaujot nevainojamu veicināšanas pārvaldību.",
        it:
          "Abbiamo realizzato un'integrazione di connessione tra il sistema di Trade Promotion Management (TPM) e Salesforce CRM. Un'agenzia di terze parti ha gestito la costruzione effettiva (dopo che abbiamo fornito guida tecnica e governance sull'integrazione, e direttamente gestito la configurazione e personalizzazione CRM). L'azienda aveva bisogno di sincronizzazione centralizzata e affidabile—senza interrompere le mani di nessun team. Il progetto ha stabilito un ponte dati ufficiale e supervisionato tra i due sistemi, garantendo coerenza dei dati e consentendo una gestione delle promozioni senza soluzione di continuità.",
      },
      keyOutcomes: [
        {
          text: {
            en:
              "Smoother promotion management: TPM reliably could now sync CRM with less recycling and fewer gaps",
            lv:
              "Vienmērīgāka veicināšanas pārvaldība: TPM tagad varēja uzticami sinhronizēt CRM ar mazāk pārstrādes un mazāk plaisu",
            it:
              "Gestione delle promozioni più fluida: TPM ora poteva sincronizzare in modo affidabile CRM con meno riciclo e meno lacune",
          },
        },
        {
          text: {
            en:
              "Eliminated error handling: Processes and validation rules in CRM based on sales rules, order clouds, TPM equal data",
            lv:
              "Novērsta kļūdu apstrāde: Procesi un validācijas noteikumi CRM, pamatojoties uz pārdošanas noteikumiem, pasūtījumu mākoņiem, TPM vienādiem datiem",
            it:
              "Gestione degli errori eliminata: Processi e regole di validazione in CRM basati su regole di vendita, cloud di ordini, dati uguali TPM",
          },
        },
        {
          text: {
            en:
              "Successful third-party (final) implementation: The roadmap was improved with data, tooling, and automation, so the business could realistically deliver over the next years",
            lv:
              "Veiksmīga trešās puses (galīgā) ieviešana: Ceļvedis tika uzlabots ar datiem, rīkiem un automatizāciju, lai bizness varētu reālistiski piegādāt nākamajos gados",
            it:
              "Implementazione di terze parti (finale) riuscita: La roadmap è stata migliorata con dati, strumenti e automazione, in modo che l'azienda potesse realisticamente consegnare nei prossimi anni",
          },
        },
        {
          text: {
            en: "Directly configured and customized Salesforce CRM",
            lv: "Tieši konfigurēts un pielāgots Salesforce CRM",
            it: "Configurato e personalizzato direttamente Salesforce CRM",
          },
        },
        {
          text: {
            en:
              "Successfully led a third-party (final) implementation that the business could realistically deliver over the next years",
            lv:
              "Veiksmīgi vadīja trešās puses (galīgo) ieviešanu, ko bizness varēja reālistiski piegādāt nākamajos gados",
            it:
              "Ha guidato con successo un'implementazione di terze parti (finale) che l'azienda poteva realisticamente consegnare nei prossimi anni",
          },
        },
      ],
      techBadges: [
        { name: "TPM (XTEL TPM)", colorClass: "bg-blue-100 text-blue-700" },
        { name: "Salesforce CRM", colorClass: "bg-indigo-100 text-indigo-700" },
        {
          name: "Microsoft Azure Cloud",
          colorClass: "bg-cyan-100 text-cyan-700",
        },
      ],
      metric: {
        value: "100%",
        label: { en: "Reliable", lv: "Uzticams", it: "Affidabile" },
        sublabel: {
          en: "data synchronization",
          lv: "datu sinhronizācija",
          it: "sincronizzazione dati",
        },
        colorClass: "text-blue-600",
        bgGradient: "from-blue-50 to-cyan-50",
      },
      iconGradient: "from-blue-500 to-cyan-600",
      iconSvgPath: ICONS.chartBar,
    },
    {
      id: "erp-crm-integration-coating",
      title: {
        en: "ERP / CRM Integration",
        lv: "ERP / CRM Integrācija",
        it: "Integrazione ERP / CRM",
      },
      industry: {
        en: "Coating Products Company",
        lv: "Pārklājuma Produktu Uzņēmums",
        it: "Azienda di Prodotti di Rivestimento",
      },
      description: {
        en:
          "We designed and built the integration between the company's legacy SAP and Salesforce. Being in sync prices, accounts, orders, data, and inventory data between SAP and Salesforce. The business depended on both SAP and Salesforce being in sync—prices, accounts, orders, inventory. The isolated data throughput quickly became the hardest part of the project.",
        lv:
          "Mēs izstrādājām un izveidojām integrāciju starp uzņēmuma mantoto SAP un Salesforce. Būt sinhronizētiem cenām, kontiem, pasūtījumiem, datiem un krājumu datiem starp SAP un Salesforce. Bizness bija atkarīgs no tā, ka gan SAP, gan Salesforce ir sinhronizēti—cenas, konti, pasūtījumi, krājumi. Izolētā datu caurlaidspēja ātri kļuva par grūtāko projekta daļu.",
        it:
          "Abbiamo progettato e costruito l'integrazione tra il legacy SAP dell'azienda e Salesforce. Essere sincronizzati prezzi, account, ordini, dati e dati di inventario tra SAP e Salesforce. L'azienda dipendeva dal fatto che sia SAP che Salesforce fossero sincronizzati—prezzi, account, ordini, inventario. Il throughput dei dati isolato è diventato rapidamente la parte più difficile del progetto.",
      },
      keyOutcomes: [
        {
          text: {
            en:
              "Bi-directional data sync: Established an official, supervised data bridge between SAP and Salesforce",
            lv:
              "Divvirzienu datu sinhronizācija: Izveidots oficiāls, uzraudzīts datu tilts starp SAP un Salesforce",
            it:
              "Sincronizzazione dati bidirezionale: Stabilito un ponte dati ufficiale e supervisionato tra SAP e Salesforce",
          },
        },
        {
          text: {
            en:
              "Operational resilience: Salesforce CRM operations didn't have the stress sales for contract, successful/failed resilience. Performance and reliability were improved",
            lv:
              "Darbības noturība: Salesforce CRM darbības nebija stresa pārdošanas līgumam, veiksmīga/neveiksmīga noturība. Veiktspēja un uzticamība tika uzlabota",
            it:
              "Resilienza operativa: Le operazioni CRM di Salesforce non avevano lo stress delle vendite per contratto, resilienza riuscita/fallita. Prestazioni e affidabilità sono state migliorate",
          },
        },
        {
          text: {
            en:
              "Improved the performance and reliability of the integration layer reduces after releases, until it could reliably move large volumes of data without falling behind",
            lv:
              "Uzlabota integrācijas slāņa veiktspēja un uzticamība samazinās pēc izlaidumiem, līdz tas varēja uzticami pārvietot lielus datu apjomus, nepaliekot atpakaļ",
            it:
              "Migliorata la prestazione e l'affidabilità del livello di integrazione si riduce dopo i rilasci, fino a quando non poteva spostare in modo affidabile grandi volumi di dati senza rimanere indietro",
          },
        },
        {
          text: {
            en:
              "Wrote the Node.js middleware responsible for syncing data and data sources to the sync without rewriting the code",
            lv:
              "Uzrakstīja Node.js starpslāni, kas atbildīgs par datu un datu avotu sinhronizāciju bez koda pārrakstīšanas",
            it:
              "Scritto il middleware Node.js responsabile della sincronizzazione dei dati e delle fonti dati alla sincronizzazione senza riscrivere il codice",
          },
        },
        {
          text: {
            en:
              "Custom integration code in Salesforce to handle the presentation layer",
            lv:
              "Pielāgots integrācijas kods Salesforce, lai apstrādātu prezentācijas slāni",
            it:
              "Codice di integrazione personalizzato in Salesforce per gestire il livello di presentazione",
          },
        },
        {
          text: {
            en:
              "Worked logic-level with duplicated data to ensure the sync could work",
            lv:
              "Strādāja loģikas līmenī ar dublētiem datiem, lai nodrošinātu, ka sinhronizācija var darboties",
            it:
              "Lavorato a livello logico con dati duplicati per garantire che la sincronizzazione potesse funzionare",
          },
        },
      ],
      techBadges: [
        { name: "Custom Node.js", colorClass: "bg-blue-100 text-blue-700" },
        { name: "MongoDB", colorClass: "bg-green-100 text-green-700" },
        { name: "SAP ERP", colorClass: "bg-orange-100 text-orange-700" },
        { name: "Salesforce CRM", colorClass: "bg-indigo-100 text-indigo-700" },
      ],
      metric: {
        value: "2x",
        label: { en: "Faster", lv: "Ātrāk", it: "Più Veloce" },
        sublabel: {
          en: "data synchronization",
          lv: "datu sinhronizācija",
          it: "sincronizzazione dati",
        },
        colorClass: "text-green-600",
        bgGradient: "from-green-50 to-emerald-50",
      },
      iconGradient: "from-green-500 to-emerald-600",
      iconSvgPath: ICONS.lightning,
    },
    {
      id: "pim-cms-integration-beverage",
      title: {
        en: "Web Product Catalog – PIM / CMS Integration",
        lv: "Tīmekļa Produktu Katalogs – PIM / CMS Integrācija",
        it: "Catalogo Prodotti Web – Integrazione PIM / CMS",
      },
      industry: {
        en: "Beverage Production Company",
        lv: "Dzērienu Ražošanas Uzņēmums",
        it: "Azienda di Produzione Bevande",
      },
      description: {
        en:
          "We implemented the tailor-made integration between the company's PIM/DAM and Headless CMS new systems. The goal was to automate how product data and assets flow from Salsify into Storyblok, enabling the CMS to display up-to-date product catalogs for tens of brands and thousands of SKUs.",
        lv:
          "Mēs ieviesām pielāgotu integrāciju starp uzņēmuma PIM/DAM un jaunajām Headless CMS sistēmām. Mērķis bija automatizēt, kā produktu dati un aktīvi plūst no Salsify uz Storyblok, ļaujot CMS attēlot aktuālus produktu katalogus desmitiem zīmolu un tūkstošiem SKU.",
        it:
          "Abbiamo implementato l'integrazione su misura tra il PIM/DAM dell'azienda e i nuovi sistemi Headless CMS. L'obiettivo era automatizzare il flusso di dati e asset dei prodotti da Salsify a Storyblok, permettendo al CMS di visualizzare cataloghi prodotti aggiornati per decine di marchi e migliaia di SKU.",
      },
      keyOutcomes: [
        {
          text: {
            en:
              "Unified content pipeline. Product data, descriptions, and images now flow seamlessly from Salsify into brand websites",
            lv:
              "Vienota satura plūsma. Produktu dati, apraksti un attēli tagad plūst nevainojami no Salsify uz zīmolu vietnēm",
            it:
              "Pipeline di contenuti unificata. Dati prodotto, descrizioni e immagini ora fluiscono senza problemi da Salsify ai siti web del marchio",
          },
        },
        {
          text: {
            en:
              "Scalable architecture. New brands or websites can be added to the middleware with minimal configuration changes",
            lv:
              "Mērogojama arhitektūra. Jaunus zīmolus vai vietnes var pievienot starpprogrammatūrai ar minimālām konfigurācijas izmaiņām",
            it:
              "Architettura scalabile. Nuovi marchi o siti web possono essere aggiunti al middleware con modifiche minime alla configurazione",
          },
        },
        {
          text: {
            en:
              "Reduced manual effort. The marketing team no longer updates product information manually across multiple CMS instances",
            lv:
              "Samazināts manuālais darbs. Mārketinga komandai vairs nav manuāli jāatjaunina produktu informācija vairākās CMS instancēs",
            it:
              "Riduzione dello sforzo manuale. Il team di marketing non aggiorna più manualmente le informazioni sui prodotti su più istanze CMS",
          },
        },
        {
          text: {
            en:
              "Design-agnostic integration. The system supports future redesigns or new brand websites without touching the data flow",
            lv:
              "Dizaina-neitrāla integrācija. Sistēma atbalsta nākotnes pārveidojumus vai jaunas zīmolu vietnes, nepieskaroties datu plūsmai",
            it:
              "Integrazione agnostica al design. Il sistema supporta futuri redesign o nuovi siti web del marchio senza toccare il flusso dati",
          },
        },
      ],
      techBadges: [
        {
          name: "Custom Node.js Middleware",
          colorClass: "bg-blue-100 text-blue-700",
        },
        { name: "MongoDB", colorClass: "bg-green-100 text-green-700" },
        {
          name: "Salsify PIM / DAM",
          colorClass: "bg-orange-100 text-orange-700",
        },
        {
          name: "Storyblok Headless CMS",
          colorClass: "bg-purple-100 text-purple-700",
        },
        { name: "React Web Admin UI", colorClass: "bg-cyan-100 text-cyan-700" },
      ],
      metric: {
        value: "10+",
        label: { en: "Brands", lv: "Zīmoli", it: "Marchi" },
        sublabel: {
          en: "integrated with automated sync",
          lv: "integrēti ar automātisko sinhronizāciju",
          it: "integrati con sincronizzazione automatica",
        },
        colorClass: "text-orange-600",
        bgGradient: "from-orange-50 to-amber-50",
      },
      iconGradient: "from-orange-500 to-amber-600",
      iconSvgPath: ICONS.lightning,
    },
    {
      id: "erp-crm-integration-medical-equipment",
      title: {
        en: "ERP / CRM Integration – Nursing & Hospital Beds Manufacturer",
        lv: "ERP / CRM Integrācija – Aprūpes un Slimnīcas Gultu Ražotājs",
        it: "Integrazione ERP / CRM – Produttore di Letti Ospedalieri",
      },
      industry: {
        en: "Medical Equipment Manufacturing",
        lv: "Medicīnas Iekārtu Ražošana",
        it: "Produzione di Apparecchiature Mediche",
      },
      description: {
        en:
          "A European manufacturer of nursing and hospital beds needed its sales teams and its operations teams to finally work off the same data. We designed and implemented the main systems integration between their ERP and CRM to synchronize customers and quotes. The middleware orchestrated the sync of data between the two systems, and stored integration state in a document database for synchronization resilience.",
        lv:
          "Eiropas aprūpes un slimnīcas gultu ražotājam bija nepieciešams, lai tā pārdošanas un operāciju komandas beidzot strādātu ar tiem pašiem datiem. Mēs izstrādājām un ieviesām galveno sistēmu integrāciju starp viņu ERP un CRM, lai sinhronizētu klientus un piedāvājumus. Starpprogrammatūra organizēja datu sinhronizāciju starp abām sistēmām un glabāja integrācijas stāvokli dokumentu datubāzē sinhronizācijas noturībai.",
        it:
          "Un produttore europeo di letti per l'assistenza e ospedalieri aveva bisogno che i suoi team di vendita e operativi lavorassero finalmente sugli stessi dati. Abbiamo progettato e implementato l'integrazione principale dei sistemi tra il loro ERP e CRM per sincronizzare clienti e preventivi. Il middleware orchestrava la sincronizzazione dei dati tra i due sistemi e memorizzava lo stato dell'integrazione in un database documentale per la resilienza della sincronizzazione.",
      },
      keyOutcomes: [
        {
          text: {
            en:
              "Single source of truth for customers. CRM and ERP now reference the same account records instead of forks",
            lv:
              "Vienots patiesības avots klientiem. CRM un ERP tagad atsaucas uz tiem pašiem konta ierakstiem, nevis dakšām",
            it:
              "Unica fonte di verità per i clienti. CRM ed ERP ora fanno riferimento agli stessi record account invece di fork",
          },
        },
        {
          text: {
            en:
              "Faster quote-to-order flow. Quotes generated by sales in CRM can move directly into ERP without manual re-entry",
            lv:
              "Ātrāka piedāvājuma-uz-pasūtījuma plūsma. Pārdošanas CRM ģenerētie piedāvājumi var pārvietoties tieši uz ERP bez manuālas atkārtotas ievades",
            it:
              "Flusso preventivo-ordine più veloce. I preventivi generati dalle vendite nel CRM possono passare direttamente all'ERP senza reinserimento manuale",
          },
        },
        {
          text: {
            en:
              "Operational clarity. Both sales and operations see the same pricing, contacts, and commercial terms for a given hospital / care provider",
            lv:
              "Operatīvā skaidrība. Gan pārdošana, gan operācijas redz tās pašas cenas, kontaktus un komerciālos noteikumus konkrētam slimnīcas / aprūpes sniedzējam",
            it:
              "Chiarezza operativa. Sia le vendite che le operazioni vedono gli stessi prezzi, contatti e termini commerciali per un dato ospedale / fornitore di assistenza",
          },
        },
        {
          text: {
            en:
              "Lower error rate. No more version mismatch between what was quoted and what production is building",
            lv:
              "Zemāks kļūdu līmenis. Vairs nav versiju neatbilstības starp piedāvāto un ražošanā būvējamo",
            it:
              "Tasso di errore inferiore. Niente più discrepanze di versione tra ciò che è stato quotato e ciò che la produzione sta costruendo",
          },
        },
      ],
      techBadges: [
        {
          name: "Custom Node.js Middleware",
          colorClass: "bg-blue-100 text-blue-700",
        },
        { name: "MongoDB", colorClass: "bg-green-100 text-green-700" },
        { name: "Salesforce CRM", colorClass: "bg-indigo-100 text-indigo-700" },
        {
          name: "Epicor ERP (API)",
          colorClass: "bg-orange-100 text-orange-700",
        },
      ],
      metric: {
        value: "1",
        label: {
          en: "Source of Truth",
          lv: "Patiesības Avots",
          it: "Fonte di Verità",
        },
        sublabel: {
          en: "for customers and quotes",
          lv: "klientiem un piedāvājumiem",
          it: "per clienti e preventivi",
        },
        colorClass: "text-indigo-600",
        bgGradient: "from-indigo-50 to-purple-50",
      },
      iconGradient: "from-indigo-500 to-purple-600",
      iconSvgPath: ICONS.lightning,
    },
  ],
};
